import { handler } from '$lib/handler';
import { Stream } from './stream';
import {
	type TranscriptFull,
	type TranscriptionEvent,
	type TranscriptionOutputEvent,
	type TranscriptOutputLineParsedTime
} from '@repo/common/types/Transcript';
import { transcriptOutput, transcripts } from '@repo/sharedlib/src/stores/transcripts';
import { parseRawOutput } from '@repo/toolslib/src/utils/timecode';
import { generateTranscriptOutput } from '@repo/toolslib/src/utils/transcript.util';

/**
 * Transcriber Store for Web
 */

// Active stream instances
let incrementalStream: Stream<TranscriptionEvent, TranscriptionOutputEvent> | null = null;
let finalStream: Stream<TranscriptionEvent, TranscriptionOutputEvent> | null = null;

// Direct event callback registry
let incrementalHandler: ((event: TranscriptionEvent) => void) | null = null;
let finalHandler: ((event: TranscriptionEvent) => void) | null = null;

let transcribeLock: null | 'incremental' | 'final' = null;
let currentAbortController: AbortController | null = null;

export const getStream = (type: 'incremental' | 'final') => {
	return type === 'incremental' ? incrementalStream : finalStream;
};

export const removeTranscribeListener = (type: 'incremental' | 'final') => {
	if (type === 'incremental') {
		incrementalHandler = null;
	} else {
		finalHandler = null;
	}
};

/**
 * Cancel the currently running transcribe operation
 * @param reason - Optional reason for cancellation
 */
export function cancelTranscribe(reason: string = 'User cancelled transcription'): boolean {
	console.log('[Transcriber] cancelTranscribe called:', reason);
	
	if (currentAbortController && !currentAbortController.signal.aborted) {
		currentAbortController.abort(reason);
	}

	// Call browser recording controller abort
	handler.recordingController.abortRecordingAction(reason);

	// Cancel the appropriate stream
	if (transcribeLock === 'incremental' && incrementalStream) {
		incrementalStream.cancel();
	} else if (transcribeLock === 'final' && finalStream) {
		finalStream.cancel();
	}

	// Force cleanup immediately to allow new transcriptions
	transcribeLock = null;
	currentAbortController = null;
	return true;
}

/**
 * Cancel the currently running transcribe operation using a command (stub for web)
 */
export async function cancelTranscribeCommand(): Promise<void> {
	console.log('[Transcriber] cancelTranscribeCommand called');
	cancelTranscribe('Command cancelled');
}

/**
 * Force cleanup of any stale transcription state
 */
export function forceCleanup(): void {
	console.log('[Transcriber] forceCleanup called');
	
	// Clean up any existing streams
	if (incrementalStream) {
		incrementalStream.remove();
		incrementalStream = null;
	}
	if (finalStream) {
		finalStream.remove();
		finalStream = null;
	}

	// Abort active recording via controller
	handler.recordingController.abortRecordingAction('Force cleanup');

	transcribeLock = null;
	currentAbortController = null;
	removeTranscribeListener('incremental');
	removeTranscribeListener('final');
}

export { finalStream, incrementalStream };

/**
 * Invoke transcription for web
 */
export async function invokeTranscribe(
	type: 'incremental' | 'final',
	invokeParams: any,
	options?: {
		timeoutMs?: number;
		needsTruncate?: boolean;
		onEvent?: (
			transcriptId: string,
			transcript: Pick<TranscriptFull, 'output' | 'rawOutput' | 'editedOutput' | 'duration'>,
			events: TranscriptOutputLineParsedTime[]
		) => void;
	}
): Promise<void> {
	if (transcribeLock) {
		throw new Error(`invokeTranscribe already in progress for type: ${transcribeLock}`);
	}

	console.log(`[Transcriber ${type}] Starting web transcribe operation:`, invokeParams);

	// Clean up any existing streams before starting
	if (incrementalStream) {
		incrementalStream.remove();
		incrementalStream = null;
	}
	if (finalStream) {
		finalStream.remove();
		finalStream = null;
	}

	// Clear any stale state before starting
	transcribeLock = null;
	currentAbortController = null;

	transcribeLock = type;

	// Create abort controller for this operation
	currentAbortController = new AbortController();
	const signal = currentAbortController.signal;

	const timeoutMs = options?.timeoutMs;
	let needsTruncate = options?.needsTruncate;

	// Create new stream for this operation
	const stream = new Stream<TranscriptionEvent, TranscriptionOutputEvent>((event, index) => ({
		...event,
		index
	}));
	
	if (type === 'incremental') {
		incrementalStream = stream;
	} else {
		finalStream = stream;
	}

	try {
		if (signal.aborted) {
			throw new Error('Transcribe operation was aborted before starting');
		}

		// Use stream's listen method with timeout
		const streamPromise = stream.listen(0, timeoutMs, (event) => {
			if (needsTruncate) {
				existingTranscriptions = [];
				existingParsed = [];
				lastStartSeconds = 0;
				needsTruncate = false;
			}
			return processEvent(type, event, options?.onEvent);
		});

		// Wait for either stream timeout or abort signal
		await Promise.race([
			streamPromise,
			new Promise<void>((_, reject) => {
				signal.addEventListener('abort', () => {
					reject(new Error(`Transcribe operation aborted: ${signal.reason}`));
				});
			})
		]);
	} catch (error) {
		const isAborted = error instanceof Error && error.message.includes('aborted');
		
		if (isAborted) {
			console.log('[Transcriber] Transcribe operation was aborted');
			cancelTranscribe('Transcribe operation was aborted');
		} else {
			console.error('[Transcriber] Error during transcribe operation:', error);
		}

		removeTranscribeListener(type);
		if (stream) {
			stream.remove();
		}
		if (type === 'incremental') {
			incrementalStream = null;
		} else {
			finalStream = null;
		}
		transcribeLock = null;
		currentAbortController = null;

		if (!isAborted) {
			throw error;
		}
	}
}

let existingTranscriptions: string[] = [];
let existingParsed: TranscriptOutputLineParsedTime[] = [];
let lastStartSeconds: number = 0;

/**
 * Process the transcription event and save to the database.
 */
export const processEvent = async (
	processType: 'incremental' | 'final',
	event: TranscriptionEvent,
	onEvent:
		| ((
			transcriptId: string,
			transcript: Pick<TranscriptFull, 'output' | 'rawOutput' | 'editedOutput' | 'duration'>,
			events: TranscriptOutputLineParsedTime[]
		) => void)
		| undefined
): Promise<void> => {
	if (!event || !Array.isArray(event.transcriptions)) {
		console.debug(
			`[Transcription ${processType}] Received invalid data, skipping processing:`,
			event
		);
		return;
	}
	if (!event.id) {
		console.debug(`[Transcription ${processType}] Missing id, skipping processing:`, event);
		return;
	}
	if (processType === 'incremental') {
		await processIncrementalEvent(event);
	} else {
		await processFinalEvent(event);
	}

	const partialTranscript = updateTranscript(event.id, existingTranscriptions, existingParsed);

	// Update the transcript output
	transcriptOutput.set([...(partialTranscript.output ?? []), { isSkeleton: true }]);

	onEvent?.(event.id, partialTranscript, existingParsed);
};

export const processIncrementalEvent = async (event: TranscriptionEvent): Promise<void> => {
	const output = event.transcriptions;
	const outputParsed = output.map((obj, i) => parseRawOutput(obj, i, true));

	const startSecondsParsed = outputParsed[0].startSeconds;
	const isMonotonic = startSecondsParsed >= lastStartSeconds;

	if (isMonotonic) {
		existingTranscriptions.push(...output);
		existingParsed.push(...outputParsed);
	} else {
		const stitchingSeamIndex = existingParsed.findIndex(
			(parsed) => parsed.startSeconds >= startSecondsParsed
		);
		existingParsed = existingParsed.slice(0, stitchingSeamIndex);
		existingTranscriptions = existingTranscriptions.slice(0, stitchingSeamIndex);

		existingTranscriptions.push(...output);
		existingParsed.push(...outputParsed);
	}

	lastStartSeconds = outputParsed[outputParsed.length - 1].endSeconds;
};

export const processFinalEvent = async (event: TranscriptionEvent): Promise<void> => {
	const output = event.transcriptions;
	const outputParsed = output.map((obj, i) => parseRawOutput(obj, i, true));

	existingTranscriptions.push(...output);
	existingParsed.push(...outputParsed);
};

export const updateTranscript = (
	transcriptId: string,
	output: string[],
	parsedOutput: TranscriptOutputLineParsedTime[]
): Pick<TranscriptFull, 'output' | 'rawOutput' | 'editedOutput' | 'duration'> => {
	const duration = parsedOutput.length > 0 ? parsedOutput[parsedOutput.length - 1].endSeconds : 0;
	const partialTranscript = {
		output: generateTranscriptOutput({ output: parsedOutput, rawOutput: output }, true),
		rawOutput: output,
		editedOutput: output,
		duration: duration
	};
	// Save the transcript to the database
	transcripts.updateTranscript(
		transcriptId,
		{
			rawOutput: partialTranscript.rawOutput,
			editedOutput: partialTranscript.editedOutput,
			output: partialTranscript.output,
			status: 'transcribed',
			duration: partialTranscript.duration
		},
		true,
		false
	);
	return partialTranscript;
};

export const processDirectEvent = (event: TranscriptionEvent, type: 'incremental' | 'final') => {
	const stream = type === 'incremental' ? incrementalStream : finalStream;
	if (stream) {
		const pushHandler = stream.create();
		pushHandler(event);
	}

	const handler = type === 'incremental' ? incrementalHandler : finalHandler;
	if (handler) {
		try {
			handler(event);
		} catch (err) {
			console.error(`[Transcriber] Error in ${type} listener:`, err);
		}
	}
};
